import React from 'react';
import { RouterProvider, createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { I18nProvider } from './contexts/I18nContext';
import { NotificationProvider } from './contexts/NotificationContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import FavoritesPage from './pages/FavoritesPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import ProfilePage from './pages/ProfilePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import AGBPage from './pages/AGBPage';
import WiderrufPage from './pages/WiderrufPage';
import VersandZahlungPage from './pages/VersandZahlungPage';
// B2C Reseller Pages
import MyShopPage from './pages/MyShopPage';
import MyShopDashboardPage from './pages/MyShopDashboardPage';
import MyShopProductsPage from './pages/MyShopProductsPage';
import MyShopMarketingPage from './pages/MyShopMarketingPage';
import OfferLandingPage from './pages/OfferLandingPage';
import MyShopOrdersPage from './pages/MyShopOrdersPage';
import MyShopAppointmentsPage from './pages/MyShopAppointmentsPage';
// Public B2C Shop Pages
import PublicShopHomePage, { PublicProductPage, PublicCartPage, B2CCartProvider } from './pages/PublicShopPage';
import PublicCheckoutPage, { PublicOrderSuccessPage } from './pages/PublicCheckoutPage';
import PublicBookingPage from './pages/PublicBookingPage';
import PublicRegisterPage from './pages/PublicRegisterPage';
import PublicLoginPage from './pages/PublicLoginPage';
// Panasonic Pages
import PanasonicWorldsPage from './pages/PanasonicWorldsPage';
import PanasonicCategoryPage from './pages/PanasonicCategoryPage';
import PanasonicProductsPage from './pages/PanasonicProductsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Wird geladen...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// B2C Shop Wrapper with Cart Provider
const B2CShopWrapper = ({ children }) => {
  const shopSlug = window.location.pathname.split('/')[2]; // Extract slug from URL
  return (
    <B2CCartProvider shopSlug={shopSlug}>
      {children}
    </B2CCartProvider>
  );
};

// Router mit Protected Routes
const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallbackPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  // Public B2C Shop Routes (no auth required)
  {
    path: "/shop/:shopSlug",
    element: <B2CShopWrapper><PublicShopHomePage /></B2CShopWrapper>,
  },
  {
    path: "/shop/:shopSlug/product/:productId",
    element: <B2CShopWrapper><PublicProductPage /></B2CShopWrapper>,
  },
  {
    path: "/shop/:shopSlug/cart",
    element: <B2CShopWrapper><PublicCartPage /></B2CShopWrapper>,
  },
  {
    path: "/shop/:shopSlug/checkout",
    element: <B2CShopWrapper><PublicCheckoutPage /></B2CShopWrapper>,
  },
  {
    path: "/shop/:shopSlug/order-success",
    element: <B2CShopWrapper><PublicOrderSuccessPage /></B2CShopWrapper>,
  },
  {
    path: "/shop/:shopSlug/booking",
    element: <PublicBookingPage />,
  },
  {
    path: "/shop/:shopSlug/register",
    element: <PublicRegisterPage />,
  },
  {
    path: "/shop/:shopSlug/login",
    element: <PublicLoginPage />,
  },
  {
    path: "/shop/:shopSlug/angebote/:campaignId/:productId",
    element: <OfferLandingPage />,
  },
  // Public routes - accessible without login
  {
    path: "/public",
    element: <PublicLayout />,
    children: [
      { path: "impressum", element: <ImpressumPage /> },
      { path: "datenschutz", element: <DatenschutzPage /> },
      { path: "agb", element: <AGBPage /> },
      { path: "widerruf", element: <WiderrufPage /> },
      { path: "versand-zahlung", element: <VersandZahlungPage /> },
    ],
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: "shop", element: <ShopPage /> },
      { path: "product/:id", element: <ProductDetailPage /> },
      { path: "favorites", element: <FavoritesPage /> },
      { path: "cart", element: <CartPage /> },
      { path: "checkout", element: <CheckoutPage /> },
      { path: "order-success", element: <OrderSuccessPage /> },
      { path: "orders", element: <OrdersPage /> },
      { path: "orders/:id", element: <OrderDetailPage /> },
      { path: "profile", element: <ProfilePage /> },
      // Panasonic Pages
      { path: "panasonic", element: <PanasonicWorldsPage /> },
      { path: "panasonic/:worldSlug", element: <PanasonicCategoryPage /> },
      { path: "panasonic/:worldSlug/:categorySlug", element: <PanasonicCategoryPage /> },
      { path: "panasonic/:worldSlug/:categorySlug/products", element: <PanasonicProductsPage /> },
      // My Shop (Reseller) Pages
      { path: "my-shop", element: <MyShopPage /> },
      { path: "my-shop/dashboard", element: <MyShopDashboardPage /> },
      { path: "my-shop/products", element: <MyShopProductsPage /> },
      { path: "my-shop/marketing", element: <MyShopMarketingPage /> },
      { path: "my-shop/orders", element: <MyShopOrdersPage /> },
      { path: "my-shop/appointments", element: <MyShopAppointmentsPage /> },
      { path: "admin/*", element: <AdminDashboard /> },
      { path: "impressum", element: <ImpressumPage /> },
      { path: "datenschutz", element: <DatenschutzPage /> },
      { path: "agb", element: <AGBPage /> },
      { path: "widerruf", element: <WiderrufPage /> },
      { path: "versand-zahlung", element: <VersandZahlungPage /> },
    ],
  },
]);

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CartProvider>
          <NotificationProvider>
            <div className="App">
              <RouterProvider router={router} />
            </div>
          </NotificationProvider>
        </CartProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
